/**
 * 공유받은 패스워드 서비스
 * 공유받은 패스워드의 CRUD 및 상태 관리를 담당
 */

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { encrypt, decrypt } from './webCryptoService';
import { 
  SHARED_PASSWORD_STATUS, 
  SHARED_PASSWORD_FIELDS,
  SHARED_PASSWORD_ERRORS,
  SHARED_PASSWORD_SUCCESS,
  SHARED_PASSWORD_EXPIRY,
  COLLECTIONS 
} from '../constants/sharedPassword';

/**
 * 공유받은 패스워드 목록 조회
 * @param {string} userId - 사용자 ID
 * @param {Object} options - 조회 옵션
 * @returns {Promise<Array>} 공유받은 패스워드 목록
 */
export const getSharedPasswords = async (userId, options = {}) => {
  if (!userId) {
    throw new Error('사용자 ID가 필요합니다.');
  }

  try {
    if (import.meta.env.DEV) {
      console.log('📥 공유받은 패스워드 목록 조회 중...', userId);
    }

    const { 
      status = null, 
      limit: itemLimit = 50, 
      sortBy = 'createdAt', 
      sortOrder = 'desc' 
    } = options;

    // 쿼리 빌드
    let q = query(
      collection(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`),
      orderBy(sortBy, sortOrder)
    );

    // 상태 필터링
    if (status && status !== 'all') {
      q = query(q, where(SHARED_PASSWORD_FIELDS.STATUS, '==', status));
    }

    // 제한 설정
    if (itemLimit > 0) {
      q = query(q, limit(itemLimit));
    }

    const querySnapshot = await getDocs(q);
    const sharedPasswords = [];

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      
      try {
        // 만료 체크
        const isExpired = data.expiresAt && data.expiresAt.toDate() < new Date();
        
        // 만료된 경우 상태 업데이트
        if (isExpired && data.status !== SHARED_PASSWORD_STATUS.EXPIRED) {
          await updateSharedPasswordStatus(userId, docSnap.id, SHARED_PASSWORD_STATUS.EXPIRED);
          data.status = SHARED_PASSWORD_STATUS.EXPIRED;
        }

        // 패스워드 데이터 복호화 (필요시)
        let passwordData = data.passwordData;
        if (import.meta.env.DEV) {
          console.log('패스워드 데이터 타입:', typeof passwordData, passwordData);
        }
        if (passwordData && typeof passwordData === 'string') {
          try {
            passwordData = JSON.parse(await decrypt(passwordData, userId));
            if (import.meta.env.DEV) {
              console.log('복호화된 패스워드 데이터:', passwordData);
            }
          } catch (decryptError) {
            console.warn('패스워드 데이터 복호화 실패:', decryptError);
            passwordData = null;
          }
        } else if (passwordData && typeof passwordData === 'object') {
          // 이미 객체인 경우 (복호화 불필요)
          if (import.meta.env.DEV) {
            console.log('패스워드 데이터가 이미 객체:', passwordData);
          }
        }

        const sharedPassword = {
          id: docSnap.id,
          sharedBy: data.sharedBy,
          sharedTo: data.sharedTo,
          shareToken: data.shareToken,
          passwordData: passwordData,
          status: data.status,
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
          readAt: data.readAt,
          acceptedAt: data.acceptedAt,
          rejectedAt: data.rejectedAt,
          shareMessage: data.shareMessage || '',
          senderName: data.senderName || '',
          senderEmail: data.senderEmail || '',
          isExpired: isExpired,
          isUnread: !data.readAt,
          timeRemaining: isExpired ? 0 : (data.expiresAt?.toDate().getTime() - Date.now())
        };

        sharedPasswords.push(sharedPassword);
      } catch (error) {
        console.error('공유받은 패스워드 처리 중 오류:', error);
        // 오류가 발생한 항목은 건너뛰기
        continue;
      }
    }

    if (import.meta.env.DEV) {
      console.log(`✅ 공유받은 패스워드 ${sharedPasswords.length}개 조회 완료`);
    }

    return sharedPasswords;
  } catch (error) {
    console.error('❌ 공유받은 패스워드 목록 조회 실패:', error);
    
    // 권한 오류인 경우 빈 배열 반환 (컬렉션이 아직 없는 경우)
    if (error.code === 'permission-denied') {
      console.log('📝 공유받은 패스워드 컬렉션이 아직 없습니다. 빈 목록을 반환합니다.');
      return [];
    }
    
    throw new Error(SHARED_PASSWORD_ERRORS.NETWORK_ERROR);
  }
};

/**
 * 공유받은 패스워드 상세 조회
 * @param {string} userId - 사용자 ID
 * @param {string} sharedPasswordId - 공유받은 패스워드 ID
 * @returns {Promise<Object>} 공유받은 패스워드 상세 정보
 */
export const getSharedPasswordById = async (userId, sharedPasswordId) => {
  if (!userId || !sharedPasswordId) {
    throw new Error('사용자 ID와 공유받은 패스워드 ID가 필요합니다.');
  }

  try {
    if (import.meta.env.DEV) {
      console.log('📄 공유받은 패스워드 상세 조회 중...', sharedPasswordId);
    }

    const docRef = doc(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`, sharedPasswordId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(SHARED_PASSWORD_ERRORS.NOT_FOUND);
    }

    const data = docSnap.data();
    
    // 만료 체크
    const isExpired = data.expiresAt && data.expiresAt.toDate() < new Date();
    
    // 만료된 경우 상태 업데이트
    if (isExpired && data.status !== SHARED_PASSWORD_STATUS.EXPIRED) {
      await updateSharedPasswordStatus(userId, sharedPasswordId, SHARED_PASSWORD_STATUS.EXPIRED);
      data.status = SHARED_PASSWORD_STATUS.EXPIRED;
    }

    // 패스워드 데이터 복호화
    let passwordData = data.passwordData;
    if (passwordData && typeof passwordData === 'string') {
      try {
        passwordData = JSON.parse(await decrypt(passwordData, userId));
      } catch (decryptError) {
        console.error('패스워드 데이터 복호화 실패:', decryptError);
        throw new Error(SHARED_PASSWORD_ERRORS.DECRYPTION_ERROR);
      }
    }

    // 읽음 상태 업데이트 (아직 읽지 않은 경우)
    if (!data.readAt) {
      await markSharedPasswordAsRead(userId, sharedPasswordId);
    }

    const sharedPassword = {
      id: docSnap.id,
      sharedBy: data.sharedBy,
      sharedTo: data.sharedTo,
      shareToken: data.shareToken,
      passwordData: passwordData,
      status: data.status,
      createdAt: data.createdAt,
      expiresAt: data.expiresAt,
      readAt: data.readAt || serverTimestamp(),
      acceptedAt: data.acceptedAt,
      rejectedAt: data.rejectedAt,
      shareMessage: data.shareMessage || '',
      senderName: data.senderName || '',
      senderEmail: data.senderEmail || '',
      isExpired: isExpired,
      isUnread: false,
      timeRemaining: isExpired ? 0 : (data.expiresAt?.toDate().getTime() - Date.now())
    };

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 상세 조회 완료');
    }

    return sharedPassword;
  } catch (error) {
    console.error('❌ 공유받은 패스워드 상세 조회 실패:', error);
    throw error;
  }
};

/**
 * 공유받은 패스워드 상태 업데이트
 * @param {string} userId - 사용자 ID
 * @param {string} sharedPasswordId - 공유받은 패스워드 ID
 * @param {string} newStatus - 새로운 상태
 * @returns {Promise<void>}
 */
export const updateSharedPasswordStatus = async (userId, sharedPasswordId, newStatus) => {
  if (!userId || !sharedPasswordId || !newStatus) {
    throw new Error('필수 파라미터가 누락되었습니다.');
  }

  try {
    if (import.meta.env.DEV) {
      console.log('🔄 공유받은 패스워드 상태 업데이트 중...', newStatus);
    }

    const docRef = doc(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`, sharedPasswordId);
    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp()
    };

    // 상태별 추가 필드 설정
    if (newStatus === SHARED_PASSWORD_STATUS.ACCEPTED) {
      updateData.acceptedAt = serverTimestamp();
    } else if (newStatus === SHARED_PASSWORD_STATUS.REJECTED) {
      updateData.rejectedAt = serverTimestamp();
    }

    await updateDoc(docRef, updateData);

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 상태 업데이트 완료');
    }
  } catch (error) {
    console.error('❌ 공유받은 패스워드 상태 업데이트 실패:', error);
    throw new Error(SHARED_PASSWORD_ERRORS.NETWORK_ERROR);
  }
};

/**
 * 공유받은 패스워드를 읽음으로 표시
 * @param {string} userId - 사용자 ID
 * @param {string} sharedPasswordId - 공유받은 패스워드 ID
 * @returns {Promise<void>}
 */
export const markSharedPasswordAsRead = async (userId, sharedPasswordId) => {
  try {
    const docRef = doc(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`, sharedPasswordId);
    await updateDoc(docRef, {
      readAt: serverTimestamp()
    });

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 읽음 처리 완료');
    }
  } catch (error) {
    console.error('❌ 공유받은 패스워드 읽음 처리 실패:', error);
    throw new Error(SHARED_PASSWORD_ERRORS.NETWORK_ERROR);
  }
};

/**
 * 공유받은 패스워드 수락
 * @param {string} userId - 사용자 ID
 * @param {string} sharedPasswordId - 공유받은 패스워드 ID
 * @param {boolean} saveToVault - 개인 패스워드 저장소에 추가할지 여부
 * @returns {Promise<void>}
 */
/**
 * 공유키로 패스워드 추가
 * @param {string} userId - 사용자 ID
 * @param {string} accessToken - 액세스 토큰
 * @param {Object} sharedData - 공유 데이터
 * @returns {Promise<string>} 생성된 공유받은 패스워드 ID
 */
export const addSharedPasswordByKey = async (userId, accessToken, sharedData) => {
  try {
    if (import.meta.env.DEV) {
      console.log('🔑 액세스 토큰으로 패스워드 추가 중...', { accessToken });
    }

    // 공유받은 패스워드 데이터 생성
    let encryptedPasswordData;
    try {
      // 패스워드 데이터 암호화 (사용자별 키로)
      encryptedPasswordData = await encrypt(JSON.stringify(sharedData.passwordData), userId);
    } catch (encryptError) {
      console.error('암호화 실패:', encryptError);
      // 암호화 실패 시 원본 데이터 그대로 사용
      encryptedPasswordData = JSON.stringify(sharedData.passwordData);
    }

    const sharedPasswordData = {
      sharedBy: sharedData.shareInfo?.sharedBy || sharedData.creatorId || '',
      sharedTo: userId,
      shareToken: accessToken, // 액세스 토큰 저장
      passwordData: encryptedPasswordData,
      status: SHARED_PASSWORD_STATUS.PENDING,
      createdAt: serverTimestamp(),
      expiresAt: sharedData.shareInfo?.expiresAt || sharedData.expiresAt,
      shareMessage: sharedData.shareMessage || '',
      senderName: sharedData.shareInfo?.sharedByName || sharedData.creatorName || '알 수 없음',
      senderEmail: sharedData.shareInfo?.sharedByEmail || sharedData.creatorEmail || '알 수 없음',
      maxUses: sharedData.maxUses || 1,
      currentUses: sharedData.uses || 0,
      securityMetadata: sharedData.shareInfo?.securityMetadata || {}
    };

    // Firestore에 저장
    try {
      const docRef = await addDoc(
        collection(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`),
        sharedPasswordData
      );
      return docRef.id;
    } catch (error) {
      // 컬렉션이 없는 경우 첫 문서 생성 시도
      if (error.code === 'permission-denied') {
        console.log('권한 오류 - 컬렉션 생성 시도');
        // setDoc으로 시도 (특정 ID로 문서 생성)
        const docId = `shared_${Date.now()}`;
        const docRef = doc(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`, docId);
        await setDoc(docRef, sharedPasswordData);
        return docId;
      }
      throw error;
    }

    if (import.meta.env.DEV) {
      console.log('✅ 공유키로 패스워드 추가 완료:', docRef.id);
    }

    return docRef.id;
  } catch (error) {
    console.error('❌ 공유키로 패스워드 추가 실패:', error);
    throw new Error(SHARED_PASSWORD_ERRORS.CREATE_FAILED);
  }
};

export const acceptSharedPassword = async (userId, sharedPasswordId, saveToVault = false) => {
  try {
    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 수락 중...', { sharedPasswordId, saveToVault });
    }

    // 상태를 수락됨으로 업데이트
    await updateSharedPasswordStatus(userId, sharedPasswordId, SHARED_PASSWORD_STATUS.ACCEPTED);

    // 개인 패스워드 저장소에 추가 (옵션)
    if (saveToVault) {
      const sharedPassword = await getSharedPasswordById(userId, sharedPasswordId);
      if (sharedPassword.passwordData) {
        // 개인 패스워드로 저장하는 로직은 추후 구현
        console.log('개인 패스워드 저장소에 추가 예정');
      }
    }

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 수락 완료');
    }
  } catch (error) {
    console.error('❌ 공유받은 패스워드 수락 실패:', error);
    throw error;
  }
};

/**
 * 공유받은 패스워드 거절
 * @param {string} userId - 사용자 ID
 * @param {string} sharedPasswordId - 공유받은 패스워드 ID
 * @param {boolean} deleteAfterReject - 거절 후 삭제할지 여부
 * @returns {Promise<void>}
 */
export const rejectSharedPassword = async (userId, sharedPasswordId, deleteAfterReject = false) => {
  try {
    if (import.meta.env.DEV) {
      console.log('❌ 공유받은 패스워드 거절 중...', { sharedPasswordId, deleteAfterReject });
    }

    // 상태를 거절됨으로 업데이트
    await updateSharedPasswordStatus(userId, sharedPasswordId, SHARED_PASSWORD_STATUS.REJECTED);

    // 거절 후 삭제 (옵션)
    if (deleteAfterReject) {
      await deleteSharedPassword(userId, sharedPasswordId);
    }

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 거절 완료');
    }
  } catch (error) {
    console.error('❌ 공유받은 패스워드 거절 실패:', error);
    throw error;
  }
};

/**
 * 공유받은 패스워드 삭제
 * @param {string} userId - 사용자 ID
 * @param {string} sharedPasswordId - 공유받은 패스워드 ID
 * @returns {Promise<void>}
 */
export const deleteSharedPassword = async (userId, sharedPasswordId) => {
  try {
    if (import.meta.env.DEV) {
      console.log('🗑️ 공유받은 패스워드 삭제 중...', sharedPasswordId);
    }

    const docRef = doc(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`, sharedPasswordId);
    await deleteDoc(docRef);

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 삭제 완료');
    }
  } catch (error) {
    console.error('❌ 공유받은 패스워드 삭제 실패:', error);
    throw new Error(SHARED_PASSWORD_ERRORS.NETWORK_ERROR);
  }
};

/**
 * 공유받은 패스워드 통계 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Object>} 통계 정보
 */
export const getSharedPasswordStats = async (userId) => {
  try {
    if (import.meta.env.DEV) {
      console.log('📊 공유받은 패스워드 통계 조회 중...', userId);
    }

    const sharedPasswords = await getSharedPasswords(userId, { limit: 0 });
    
    const stats = {
      total: sharedPasswords.length,
      pending: sharedPasswords.filter(sp => sp.status === SHARED_PASSWORD_STATUS.PENDING).length,
      accepted: sharedPasswords.filter(sp => sp.status === SHARED_PASSWORD_STATUS.ACCEPTED).length,
      rejected: sharedPasswords.filter(sp => sp.status === SHARED_PASSWORD_STATUS.REJECTED).length,
      expired: sharedPasswords.filter(sp => sp.status === SHARED_PASSWORD_STATUS.EXPIRED).length,
      unread: sharedPasswords.filter(sp => sp.isUnread).length,
      expiringSoon: sharedPasswords.filter(sp => 
        sp.timeRemaining > 0 && sp.timeRemaining < (2 * 60 * 60 * 1000) // 2시간 이내
      ).length
    };

    if (import.meta.env.DEV) {
      console.log('✅ 공유받은 패스워드 통계 조회 완료:', stats);
    }

    return stats;
  } catch (error) {
    console.error('❌ 공유받은 패스워드 통계 조회 실패:', error);
    
    // 권한 오류인 경우 기본 통계 반환
    if (error.code === 'permission-denied') {
      console.log('📝 공유받은 패스워드 통계를 위한 기본값을 반환합니다.');
      return {
        total: 0,
        pending: 0,
        accepted: 0,
        rejected: 0,
        expired: 0,
        unread: 0,
        expiringSoon: 0
      };
    }
    
    throw new Error(SHARED_PASSWORD_ERRORS.NETWORK_ERROR);
  }
};

/**
 * 만료된 공유받은 패스워드 정리
 * @param {string} userId - 사용자 ID
 * @param {number} olderThanDays - 며칠 이전의 만료된 항목을 삭제할지 (기본: 30일)
 * @returns {Promise<number>} 삭제된 항목 수
 */
export const cleanupExpiredSharedPasswords = async (userId, olderThanDays = 30) => {
  try {
    if (import.meta.env.DEV) {
      console.log('🧹 만료된 공유받은 패스워드 정리 중...', { userId, olderThanDays });
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

    const q = query(
      collection(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`),
      where(SHARED_PASSWORD_FIELDS.STATUS, '==', SHARED_PASSWORD_STATUS.EXPIRED),
      where(SHARED_PASSWORD_FIELDS.EXPIRES_AT, '<', Timestamp.fromDate(cutoffDate))
    );

    const querySnapshot = await getDocs(q);
    let deletedCount = 0;

    for (const docSnap of querySnapshot.docs) {
      await deleteDoc(docSnap.ref);
      deletedCount++;
    }

    if (import.meta.env.DEV) {
      console.log(`✅ 만료된 공유받은 패스워드 ${deletedCount}개 정리 완료`);
    }

    return deletedCount;
  } catch (error) {
    console.error('❌ 만료된 공유받은 패스워드 정리 실패:', error);
    throw new Error(SHARED_PASSWORD_ERRORS.NETWORK_ERROR);
  }
};

/**
 * 공유 토큰으로 공유받은 패스워드 조회
 * @param {string} userId - 사용자 ID
 * @param {string} shareToken - 공유 토큰
 * @returns {Promise<Object>} 공유받은 패스워드 정보
 */
export const getSharedPasswordByToken = async (userId, shareToken) => {
  if (!userId || !shareToken) {
    throw new Error('사용자 ID와 공유 토큰이 필요합니다.');
  }

  try {
    if (import.meta.env.DEV) {
      console.log('🔍 공유 토큰으로 패스워드 조회 중...', shareToken);
    }

    const q = query(
      collection(db, `users/${userId}/${COLLECTIONS.SHARED_PASSWORDS}`),
      where(SHARED_PASSWORD_FIELDS.SHARE_TOKEN, '==', shareToken),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error(SHARED_PASSWORD_ERRORS.INVALID_TOKEN);
    }

    const docSnap = querySnapshot.docs[0];
    return await getSharedPasswordById(userId, docSnap.id);
  } catch (error) {
    console.error('❌ 공유 토큰으로 패스워드 조회 실패:', error);
    throw error;
  }
};

// 기본 내보내기
export default {
  getSharedPasswords,
  getSharedPasswordById,
  updateSharedPasswordStatus,
  markSharedPasswordAsRead,
  acceptSharedPassword,
  rejectSharedPassword,
  deleteSharedPassword,
  getSharedPasswordStats,
  cleanupExpiredSharedPasswords,
  getSharedPasswordByToken
};